import {
  Button,
  Flex,
  Grid,
  Icon,
  Input,
  Link,
  ModalManagerContext,
  ModalMethods,
  Option,
  Select,
  Text,
  TextArea,
} from '@pega/cosmos-react-core';
import React, { useContext, useRef } from 'react';
import styled, { css } from 'styled-components';
import { languageMapping } from '../../constants/languageCodes';
import useLocalTranslation from '../../hooks/useLocalTranslation';
import AddChatHeadingModal from '../SocialMedia/Chat/Modals/AddChatHeadingModal';
import { CustomModal } from './CustomModal';
import { customModalProps } from '../../interfaces/common';
import { contentType } from '../SocialMedia/Chat/Tabs/GeneralTab';

interface MultiLocaleProp {
  isForChatHeadingOrSpecialNote: string;
  addHeading: (l: string, h: string) => void;
  titleSpecialNoteData: any;
  allowedFilteredLanguages: any;
  defaultLanguage: string;
  setValues: Function;
  allowNewField: boolean;
  supportedLanguagesUpdated: any;
  showTextArea: boolean;
  specialNoteError?: { [key: string]: { hasError: boolean } };
  setSpecialNoteError?: Function;
}

export const linkTextStyle = {
  width: 'fit-content',
  cursor: 'pointer',
  color: '#0076D1',
  fontSize: '0.8125rem',
};

export const StyledLinkHover = styled.h5(
  () =>
    css`
      :hover {
        text-decoration: underline;
      }
    `,
);

const MultiLocaleTitleAndSpecialNote: React.FC<MultiLocaleProp> = (props) => {
  const {
    isForChatHeadingOrSpecialNote,
    addHeading,
    titleSpecialNoteData,
    allowedFilteredLanguages,
    defaultLanguage,
    setValues,
    allowNewField,
    supportedLanguagesUpdated,
    showTextArea = false,
    specialNoteError,
    setSpecialNoteError,
  } = props;
  const { create } = useContext(ModalManagerContext);
  const translate = useLocalTranslation();
  const addNewHeadingAndSpecialNote = useRef<ModalMethods<Partial<customModalProps>>>();

  const heading =
    isForChatHeadingOrSpecialNote === contentType.Chat ? translate('chat_headings') : translate('special_notes');
  const firstSubHeader =
    isForChatHeadingOrSpecialNote === contentType.Chat ? translate('language') : translate('language');
  const secondSubHeader =
    isForChatHeadingOrSpecialNote === contentType.Chat ? translate('chat_heading') : translate('special_note');
  const dialogHeading =
    isForChatHeadingOrSpecialNote === contentType.Chat ? translate('add_chat_heading') : translate('add_special_notes');
  const diffLangConfigLink =
    isForChatHeadingOrSpecialNote === contentType.Chat
      ? translate('add_chat_heading_for_another_language')
      : translate('add_special_note_for_another_language');

  const addNewConfigDialog = () => {
    addNewHeadingAndSpecialNote.current = create(CustomModal, {
      heading: dialogHeading,
      children: (
        <AddChatHeadingModal
          languages={supportedLanguagesUpdated.filter((l: any) => !Object.keys(titleSpecialNoteData).includes(l))}
          addHeading={addHeading}
          showTextArea={showTextArea}
        />
      ),
    });
  };

  const iconButtonStyle = {
    height: 20,
    width: 20,
    marginTop: isForChatHeadingOrSpecialNote === contentType.SpecialNote ? '1.3rem' : 0,
  };

  return (
    <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
      <Text variant="h3" as="h3">
        {heading}
      </Text>
      {allowedFilteredLanguages.map((lang: any, i: number) => (
        <Grid
          container={{
            cols: 'repeat(3, 30% 60% 10%)',
            gap: 2,
            alignItems: isForChatHeadingOrSpecialNote === contentType.SpecialNote ? 'start' : 'end',
          }}
          key={lang}
        >
          <Select label={firstSubHeader} value={lang} onChange={() => {}}>
            <Option value={lang}>{languageMapping[lang]}</Option>
          </Select>
          {showTextArea ? (
            <Flex>
              <TextArea
                label={secondSubHeader}
                value={titleSpecialNoteData?.[lang]}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => addHeading(lang, e.target.value)}
                required={true}
                autoResize={true}
                status={specialNoteError?.[lang]?.hasError ? 'error' : undefined}
              />
              {specialNoteError?.[lang]?.hasError && (
                <Text status="error">{translate('special_note_validation_error')}</Text>
              )}
            </Flex>
          ) : (
            <Input
              label={secondSubHeader}
              type="text"
              autoComplete="off"
              key={`lang${i + 1}`}
              value={titleSpecialNoteData?.[lang]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => addHeading(lang, e.target.value)}
            />
          )}
          {lang !== defaultLanguage && (
            <Button
              onClick={() => {
                const newData = { ...titleSpecialNoteData };
                delete newData[lang];
                setValues({ ...newData });
                if (isForChatHeadingOrSpecialNote === contentType.SpecialNote && setSpecialNoteError) {
                  const newSpecialNoteErr = { ...specialNoteError };
                  delete newSpecialNoteErr[lang];
                  setSpecialNoteError({ ...newSpecialNoteErr });
                }
              }}
              icon
              style={iconButtonStyle}
            >
              <Icon name={'trash'} style={{ height: 20, width: 20 }} />
            </Button>
          )}
        </Grid>
      ))}
      {allowNewField && (
        <Link href="" as={StyledLinkHover} style={linkTextStyle} onClick={addNewConfigDialog}>
          {diffLangConfigLink}
        </Link>
      )}
    </Flex>
  );
};

export default MultiLocaleTitleAndSpecialNote;
